import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {IStore} from 'redux/interface';
import {dialogueAsyncActions, getBotById, getBotVideoAvatar} from 'redux/pageAsyncActions';
import {getBotByIdKey, getBotVideoAvatarKey} from 'redux/dialogue/dialogueAsyncActions';
import {IBot} from 'utils/hooks/use-chat-bot/interfaces';
import {useLoadFile} from 'utils/hooks/useLoadFile';

interface IBotVideoAvatar {
  avatarName?: string
  avatarImageUrl?: string
  isAvatarLoaded: boolean
}

interface IAvatarDialogueBot extends IBotVideoAvatar {
  bot: IBot | null
  isLoaded: boolean
}

// The store holds the last bot and avatar loaded, which may belong to another dialogue, so each
// record is used only when its id matches. A load counts as finished once the matching record
// arrives or its load has failed; on failure the surface falls back to default settings, and to
// the title as the avatar name.

function useBotVideoAvatar(avatarId?: number | null): IBotVideoAvatar {
  const dispatch = useDispatch();
  const {getFileUrl, media} = useLoadFile();

  const {botVideoAvatar} = useSelector((state: IStore) => state.dialogue);
  const {errors} = useSelector((state: IStore) => state.status);

  const avatar = avatarId && botVideoAvatar?.id === avatarId ? botVideoAvatar : null;
  const imageId = avatar?.imageId;
  const avatarImageUrl = imageId ? media[imageId] : undefined;
  const isFailed = !!avatarId && !!errors[getBotVideoAvatarKey(avatarId)];
  const isImageLoaded = !imageId || !!avatarImageUrl || !!errors[imageId.toString()];

  useEffect(() => {
    if (avatarId) {
      dispatch(dialogueAsyncActions[getBotVideoAvatar](avatarId));
    }
  }, [avatarId]);

  useEffect(() => {
    if (imageId) {
      getFileUrl(imageId);
    }
  }, [imageId]);

  return {
    avatarName: avatar?.name,
    avatarImageUrl: avatarImageUrl || undefined,
    isAvatarLoaded: !avatarId || isFailed || (!!avatar && isImageLoaded)
  };
}

export function useAvatarDialogueBot(botId: number, avatarId?: number | null): IAvatarDialogueBot {
  const dispatch = useDispatch();
  const {bot: storedBot} = useSelector((state: IStore) => state.dialogue);
  const {errors} = useSelector((state: IStore) => state.status);
  const videoAvatar = useBotVideoAvatar(avatarId);

  const bot = storedBot?.id === botId ? storedBot : null;
  const isBotLoaded = !!bot || !!errors[getBotByIdKey(botId)];

  useEffect(() => {
    dispatch(dialogueAsyncActions[getBotById](botId));
  }, [botId]);

  return {
    ...videoAvatar,
    bot,
    isLoaded: isBotLoaded && videoAvatar.isAvatarLoaded
  };
}
